import { useEffect, useRef, useState } from 'react';

/* ---------- shared helpers ---------- */

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);
  return reduced;
}

// Reveal + animate only once the element scrolls into view.
function useInView<T extends Element>() {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); io.disconnect(); } },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, inView };
}

// Signal palette: red (bad) → amber (mixed) → emerald (quality).
function scoreColor(score: number) {
  if (score >= 70) return '#059669'; // emerald-600
  if (score >= 45) return '#d97706'; // amber-600
  return '#dc2626'; // red-600
}

/* ---------- ScoreGauge: 270° instrument dial, 0–100 ---------- */

interface ScoreGaugeProps {
  score: number;
  size?: number;
  label?: string;
  color?: string;
}

export function ScoreGauge({ score, size = 220, label = 'SCORE / 100', color }: ScoreGaugeProps) {
  const reduced = useReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>();
  const [display, setDisplay] = useState(reduced ? score : 0);

  const clamped = Math.max(0, Math.min(100, score));
  const stroke = color ?? scoreColor(clamped);

  const r = 50;
  const c = 2 * Math.PI * r;
  const sweep = 0.75; // 270° of the circle is the track
  const track = c * sweep;
  const active = inView || reduced ? (clamped / 100) * track : 0;

  // Count-up the numeral in sync with the arc.
  useEffect(() => {
    if (reduced || !inView) { setDisplay(clamped); return; }
    let raf = 0;
    const start = performance.now();
    const dur = 900;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * clamped));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, reduced, clamped]);

  return (
    <div ref={ref} className="inline-flex flex-col items-center" style={{ width: size }}>
      <svg viewBox="0 0 120 120" width={size} height={size} role="img"
        aria-label={`${label}: ${clamped}`}>
        {/* track */}
        <circle
          cx="60" cy="60" r={r} fill="none" stroke="#e7e5e4" strokeWidth="9"
          strokeLinecap="round" strokeDasharray={`${track} ${c}`}
          transform="rotate(135 60 60)"
        />
        {/* value */}
        <circle
          cx="60" cy="60" r={r} fill="none" stroke={stroke} strokeWidth="9"
          strokeLinecap="round" strokeDasharray={`${active} ${c}`}
          transform="rotate(135 60 60)"
          style={{ transition: reduced ? undefined : 'stroke-dasharray 900ms cubic-bezier(0.22,1,0.36,1)' }}
        />
        <text x="60" y="58" textAnchor="middle" dominantBaseline="middle"
          style={{ font: '700 34px "IBM Plex Mono", monospace', fill: '#1c1917', fontVariantNumeric: 'tabular-nums' }}>
          {display}
        </text>
        <text x="60" y="78" textAnchor="middle" dominantBaseline="middle"
          style={{ font: '500 8px "IBM Plex Mono", monospace', fill: '#78716c', letterSpacing: '0.14em' }}>
          {label}
        </text>
      </svg>
    </div>
  );
}

/* ---------- MetricBar: labelled horizontal meter ---------- */

interface MetricBarProps {
  label: string;
  value: number;      // 0–100 (percentage fill)
  display?: string;   // text shown at right, defaults to `${value}%`
  tone?: 'good' | 'bad' | 'neutral';
}

export function MetricBar({ label, value, display, tone = 'neutral' }: MetricBarProps) {
  const reduced = useReducedMotion();
  const { ref, inView } = useInView<HTMLDivElement>();
  const pct = Math.max(0, Math.min(100, value));
  const fill = inView || reduced ? pct : 0;
  const barColor = tone === 'good' ? '#059669' : tone === 'bad' ? '#dc2626' : '#57534e';

  return (
    <div ref={ref} className="w-full">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-sm text-stone-700">{label}</span>
        <span className="font-mono text-sm font-semibold text-stone-900 tabular-nums">
          {display ?? `${pct}%`}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-stone-200 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${fill}%`,
            background: barColor,
            transition: reduced ? undefined : 'width 900ms cubic-bezier(0.22,1,0.36,1)',
          }}
        />
      </div>
    </div>
  );
}
